import { useMemo, useRef, useState, useEffect } from "react";
import type { Meal } from "@/data/meal-plan";
import type { ChoiceGroupSelections, MealSelections } from "@/types/meal-plan-state";
import {
  getMealChoices,
  getMealMenu,
  isChoiceGroupComplete,
  isFixedGroup,
} from "@/lib/random-plan";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useOptionalWeekPlan } from "@/context/week-plan-context";
import { hasEatenMarks, withEatenLossWarning } from "@/lib/owner-eaten";
import { FoodOptionPicker } from "./food-option-picker";
import { MealMenuPicker } from "./meal-menu-picker";

interface MealCardProps {
  meal: Meal;
  selections: MealSelections;
  onSelectionChange: (
    choiceGroupIndex: number,
    selections: ChoiceGroupSelections,
  ) => void;
  note?: string;
  onNoteChange?: (note: string) => void;
  /** The day's chosen menu for this meal (`mealMenus[mealIndex]`), for menu meals */
  menuId?: string;
  /** Choose a menu, or un-choose it with `null` */
  onMenuChange?: (menuId: string | null) => void;
}

const EMPTY_GROUP_SELECTIONS: ChoiceGroupSelections = { selections: [], skipped: false };

export function MealCard({
  meal,
  selections,
  onSelectionChange,
  note,
  onNoteChange,
  menuId,
  onMenuChange,
}: MealCardProps) {
  const choices = getMealChoices(meal, menuId);
  const needsMenu = !!meal.menus && !getMealMenu(meal, menuId);
  const completedCount = choices.filter((group, i) => {
    const sel = selections[i];
    return !!sel && isChoiceGroupComplete(group, sel);
  }).length;
  const totalCount = choices.length;
  const isComplete = completedCount === totalCount;

  // Switching or removing the menu restarts the meal; confirm only when picks
  // beyond the menu's fixed foods would be lost. `menuId: null` = remove.
  const [menuConfirm, setMenuConfirm] = useState<{ open: boolean; menuId: string | null }>({
    open: false,
    menuId: null,
  });
  const hasOwnPicks = choices.some((group, i) => {
    const sel = selections[i];
    return !isFixedGroup(group) && !!sel && (sel.selections.length > 0 || sel.skipped);
  });
  function handleMenuSelect(id: string | null) {
    if (id === (menuId ?? null)) return;
    if (hasOwnPicks) setMenuConfirm({ open: true, menuId: id });
    else onMenuChange?.(id);
  }
  const confirmMenu = menuConfirm.menuId ? getMealMenu(meal, menuConfirm.menuId) : undefined;
  const owner = useOptionalWeekPlan()?.owner;
  const menuConfirmDescription = "Se perderán las selecciones de esta comida.";

  const mealTotals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0, ingredientCount: 0 };
    for (let ci = 0; ci < choices.length; ci++) {
      const group = choices[ci]!;
      const cgSels = selections[ci];
      if (!cgSels || cgSels.skipped) continue;
      for (const sel of cgSels.selections) {
        const opt = group.options.find((o) => o.id === sel.optionId);
        if (!opt?.nutrition) continue;
        const p = opt.nutrition.perPortion;
        t.calories += p.calories * sel.count;
        t.protein += p.protein * sel.count;
        t.carbs += p.carbs * sel.count;
        t.fat += p.fat * sel.count;
        t.ingredientCount += sel.count;
      }
    }
    t.calories = Math.round(t.calories);
    t.protein = Math.round(t.protein * 10) / 10;
    t.carbs = Math.round(t.carbs * 10) / 10;
    t.fat = Math.round(t.fat * 10) / 10;
    return t;
  }, [choices, selections]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(note ?? "");
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  // Sync note text when prop changes externally
  useEffect(() => { setNoteText(note ?? ""); }, [note]);

  function handleNoteSave() {
    onNoteChange?.(noteText);
    setEditingNote(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>{meal.label}</span>
          {meal.time && (
            <span className="text-xs font-normal text-muted-foreground">{meal.time}</span>
          )}
          {!needsMenu && (
            <span
              className={`text-xs font-normal px-2 py-0.5 rounded-full ${
                isComplete
                  ? "bg-green-100 text-green-700"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {completedCount}/{totalCount}
            </span>
          )}
          {/* Three-dot menu (only when note editing is supported) */}
          {onNoteChange && (
            <div className="relative ml-auto" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Opciones de comida"
                className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <circle cx="8" cy="3" r="1.5" />
                  <circle cx="8" cy="8" r="1.5" />
                  <circle cx="8" cy="13" r="1.5" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-background border rounded-md shadow-md py-1 min-w-[140px]">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                    onClick={() => {
                      setEditingNote(true);
                      setMenuOpen(false);
                    }}
                  >
                    {note ? "Editar nota" : "Agregar nota"}
                  </button>
                  {meal.menus && !needsMenu && onMenuChange && (
                    <button
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                      onClick={() => {
                        setMenuOpen(false);
                        handleMenuSelect(null);
                      }}
                    >
                      Quitar opción
                    </button>
                  )}
                  {note && (
                    <button
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors text-red-600"
                      onClick={() => {
                        onNoteChange?.("");
                        setMenuOpen(false);
                      }}
                    >
                      Eliminar nota
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardTitle>
        {meal.note && (
          <p className="text-xs text-muted-foreground mt-1">{meal.note}</p>
        )}
        {/* Note display */}
        {note && !editingNote && (
          <p className="text-xs text-muted-foreground italic mt-1">
            {note}
          </p>
        )}
        {/* Note editor */}
        {editingNote && (
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleNoteSave(); if (e.key === "Escape") setEditingNote(false); }}
              placeholder="ej. estofado, lomo saltado..."
              className="flex-1 text-sm border rounded-md px-2 py-1 bg-background"
              autoFocus
            />
            <button
              type="button"
              onClick={handleNoteSave}
              className="text-xs px-2 py-1 rounded-md bg-primary text-primary-foreground"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => { setEditingNote(false); setNoteText(note ?? ""); }}
              className="text-xs px-2 py-1 rounded-md border"
            >
              Cancelar
            </button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {meal.menus && onMenuChange && (
          <MealMenuPicker menus={meal.menus} value={menuId} onSelect={handleMenuSelect} />
        )}
        {choices.map((group, groupIndex) => (
          <FoodOptionPicker
            key={`${menuId ?? ""}-${groupIndex}`}
            group={group}
            selections={selections[groupIndex] ?? EMPTY_GROUP_SELECTIONS}
            onChange={(s) => onSelectionChange(groupIndex, s)}
            fixed={!!meal.menus && isFixedGroup(group)}
          />
        ))}
        {mealTotals.ingredientCount > 1 && (
          <div className="flex items-center justify-between pt-2 border-t text-xs tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground/80">
              {mealTotals.calories} <span className="font-normal">kcal</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-red-600/80">{mealTotals.protein}<span className="text-muted-foreground ml-0.5">P</span></span>
              <span className="text-amber-600/80">{mealTotals.carbs}<span className="text-muted-foreground ml-0.5">C</span></span>
              <span className="text-purple-600/80">{mealTotals.fat}<span className="text-muted-foreground ml-0.5">G</span></span>
            </div>
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={menuConfirm.open}
        onOpenChange={(open) => setMenuConfirm((prev) => ({ ...prev, open }))}
        title={
          menuConfirm.menuId
            ? `¿Cambiar a ${confirmMenu?.name ?? "otra opción"}?`
            : "¿Quitar la opción elegida?"
        }
        description={
          owner ? withEatenLossWarning(menuConfirmDescription, owner, hasEatenMarks([selections])) : menuConfirmDescription
        }
        confirmText={menuConfirm.menuId ? "Cambiar" : "Quitar"}
        onConfirm={() => onMenuChange?.(menuConfirm.menuId)}
      />
    </Card>
  );
}
